import { animate } from "@theme/m";
import * as compareStoreModule from "./sn-compare-store.js";
import {
  compareDebug,
  fetchCompareProductJson,
  resolveVariantIdByImage,
  resolveVariantIdByOptionValue,
  resolveVariantIdForCompareItem
} from "./sn-compare-variant.js";

const {
  snCompareStore,
  CHANGE_EVENT,
  MAX_ITEMS,
  toRootPath,
  sanitizeHandle,
  parseHandlesFromUrl,
  syncUrlFromStore,
  parseCollectionHandles
} = compareStoreModule;

const saveLastEligibleCompareCollectionUrl =
  compareStoreModule.saveLastEligibleCompareCollectionUrl || (() => false);

function getCompareStrings() {
  return window.themeHDN?.strings?.compare || {};
}

function captureLastCompareCollectionUrl() {
  if (typeof document === "undefined") {
    return;
  }

  const capture = () => {
    const collectionRoot = document.querySelector("[data-sn-compare-collection-handle]");
    const collectionHandle = sanitizeHandle(collectionRoot?.dataset?.snCompareCollectionHandle);
    if (!collectionHandle) {
      return;
    }
    saveLastEligibleCompareCollectionUrl(collectionHandle);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", capture, { once: true });
    return;
  }

  capture();
}

captureLastCompareCollectionUrl();

function updateCompareStoreItem(handle, updates = {}) {
  if (typeof snCompareStore.updateItem === "function") {
    return snCompareStore.updateItem(handle, updates);
  }

  const normalized = sanitizeHandle(handle);
  if (!normalized) {
    return false;
  }

  const items = snCompareStore.getItems();
  const index = items.findIndex((item) => item.handle === normalized);
  if (index === -1) {
    return false;
  }

  const current = items[index];
  const variantId =
    updates.variantId != null ? String(updates.variantId).trim() : current.variantId;
  const baseUrl = (updates.url || current.url || toRootPath(`/products/${normalized}`)).split("?")[0];
  const nextItem = {
    ...current,
    ...updates,
    handle: normalized,
    variantId,
    url: variantId ? appendVariantParam(baseUrl, variantId) : baseUrl,
    image: updates.image != null ? String(updates.image).trim() : current.image
  };

  if (
    current.variantId === nextItem.variantId &&
    current.image === nextItem.image &&
    current.url === nextItem.url &&
    current.title === nextItem.title
  ) {
    return false;
  }

  const nextItems = [...items.slice(0, index), nextItem, ...items.slice(index + 1)];
  return snCompareStore.setItems(nextItems);
}

function appendVariantParam(url, variantId) {
  const baseUrl = String(url || "").trim();
  const id = String(variantId || "").trim();
  if (!baseUrl || !id) {
    return baseUrl;
  }

  try {
    const parsed = new URL(baseUrl, window.location.origin);
    parsed.searchParams.set("variant", id);
    return `${parsed.pathname}${parsed.search}`;
  } catch (error) {
    return baseUrl;
  }
}

const ITEM_HEIGHT = 56;
const ITEM_GAP = 8;
const STACK_PEEK = 8;
const STACK_HOVER_PEEK = 12;
const STACK_HOVER_LIFT = 6;
const STACK_TOP_CLEARANCE = 8;
const STACK_BOTTOM_CLEARANCE = 12;
const VALID_MODES = new Set(["expanded", "truncated"]);
const SPRING = { type: "spring", stiffness: 600, damping: 50 };
const STACK_SPRING = { type: "spring", mass: 0.7 };
const HOVER_SPRING = { type: "spring", stiffness: 420, damping: 34 };

function sanitizeTitle(title, handle) {
  const text = String(title || "").trim();
  return text || handle;
}

function resetSlotMotionStyles(slot) {
  if (!slot) {
    return;
  }
  slot.style.height = "";
  slot.style.marginTop = "";
  slot.style.marginBottom = "";
  slot.style.overflow = "";
  slot.style.transform = "";
  slot.style.opacity = "";
  slot.style.width = "";
}

function resetListMotionStyles(list) {
  if (!list) {
    return;
  }
  list.style.height = "";
  list.style.marginTop = "";
  list.style.paddingTop = "";
}

function toAnimationPromise(animation) {
  if (animation?.finished && typeof animation.finished.then === "function") {
    return animation.finished;
  }
  if (animation && typeof animation.then === "function") {
    return animation;
  }
  return Promise.resolve();
}

async function enrichStoreItemVariant(item) {
  if (!item?.handle) {
    return false;
  }

  if (item.variantId) {
    compareDebug("enrich:skip-has-variant", item);
    return false;
  }

  const product = await fetchCompareProductJson(item.handle, toRootPath);
  const variantId = resolveVariantIdForCompareItem(item, product);

  if (!variantId) {
    console.warn("[sn-compare] enrich:failed — could not resolve variantId", {
      handle: item.handle,
      image: item.image,
      url: item.url
    });
    return false;
  }

  compareDebug("enrich:success", { handle: item.handle, variantId, image: item.image });
  return updateCompareStoreItem(item.handle, {
    ...item,
    variantId,
    url: appendVariantParam(
      (item.url || toRootPath(`/products/${item.handle}`)).split("?")[0],
      variantId
    )
  });
}

class SnCompareCheckbox extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleStoreChange = this.handleStoreChange.bind(this);
    this.handleSwatchChange = this.handleSwatchChange.bind(this);
  }

  connectedCallback() {
    this.setAttribute("role", "checkbox");
    this.card = this.closest("hdt-card-product");
    this.addEventListener("click", this.handleClick);
    this.addEventListener("keydown", this.handleKeydown);
    document.addEventListener(CHANGE_EVENT, this.handleStoreChange);
    this.card?.addEventListener("change", this.handleSwatchChange);
    this.syncFromSelectedSwatch();
    this.syncFromStore();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener(CHANGE_EVENT, this.handleStoreChange);
    this.card?.removeEventListener("change", this.handleSwatchChange);
  }

  get itemHandle() {
    return sanitizeHandle(this.dataset.handle);
  }

  get itemTitle() {
    return sanitizeTitle(this.dataset.title, this.itemHandle);
  }

  get itemCollections() {
    return parseCollectionHandles(this.dataset.collections);
  }

  get pageCollectionHandle() {
    const root = this.closest("[data-sn-compare-collection-handle]");
    return root ? sanitizeHandle(root.dataset.snCompareCollectionHandle) : "";
  }

  buildItem() {
    return {
      handle: this.itemHandle,
      id: this.dataset.id || "",
      variantId: this.dataset.variantId || "",
      title: this.itemTitle,
      image: this.dataset.image || "",
      url: this.dataset.url || toRootPath(`/products/${this.itemHandle}`),
      collections: this.itemCollections
    };
  }

  getProductJson() {
    if (!this._productJsonPromise) {
      this._productJsonPromise = fetchCompareProductJson(this.itemHandle, toRootPath);
    }
    return this._productJsonPromise;
  }

  async resolveVariantIdFromSwatch(radio) {
    if (radio?.dataset?.vid) {
      return String(radio.dataset.vid);
    }

    const product = await this.getProductJson();
    if (product && radio?.value) {
      const fromOption = resolveVariantIdByOptionValue(product, radio.value);
      if (fromOption) {
        return fromOption;
      }
    }

    if (product && radio?.dataset?.vimg) {
      const fromImage = resolveVariantIdByImage(product, radio.dataset.vimg);
      if (fromImage) {
        return fromImage;
      }
    }

    const fromMedia = this.readImageFromCardMedia();
    if (product && fromMedia) {
      const fromMediaMatch = resolveVariantIdByImage(product, fromMedia);
      if (fromMediaMatch) {
        return fromMediaMatch;
      }
    }

    return this.readVariantIdFromCardLink();
  }

  async applySwatchDataAsync(radio) {
    const { vimg, url } = radio?.dataset || {};
    const vid = radio ? await this.resolveVariantIdFromSwatch(radio) : "";

    if (vid) {
      this.dataset.variantId = vid;
    }

    const image = vimg || this.readImageFromCardMedia();
    if (image) {
      this.dataset.image = image;
    }

    const baseUrl =
      url ||
      this.readCardProductUrl() ||
      this.dataset.url ||
      toRootPath(`/products/${this.itemHandle}`);
    this.dataset.url = vid ? appendVariantParam(baseUrl.split("?")[0], vid) : baseUrl;

    compareDebug("checkbox:apply-swatch", {
      handle: this.itemHandle,
      variantId: this.dataset.variantId,
      image: this.dataset.image,
      url: this.dataset.url,
      radioValue: radio?.value
    });
  }

  async syncFromSelectedSwatchAsync() {
    const checkedSwatch = this.card?.querySelector('wrapp-hdt-swatches [type="radio"]:checked');
    if (checkedSwatch) {
      await this.applySwatchDataAsync(checkedSwatch);
      return;
    }

    const product = await this.getProductJson();
    const image = this.readImageFromCardMedia();
    let vid = this.readVariantIdFromCardLink();

    if (!vid && product && image) {
      vid = resolveVariantIdByImage(product, image);
    }

    if (vid) {
      this.dataset.variantId = vid;
      const baseUrl =
        this.readCardProductUrl() ||
        this.dataset.url ||
        toRootPath(`/products/${this.itemHandle}`);
      this.dataset.url = appendVariantParam(baseUrl.split("?")[0], vid);
    }

    if (image) {
      this.dataset.image = image;
    }
  }

  syncFromSelectedSwatch() {
    const checkedSwatch = this.card?.querySelector('wrapp-hdt-swatches [type="radio"]:checked');
    if (checkedSwatch) {
      this.applySwatchData(checkedSwatch);
      return;
    }

    const vid = this.readVariantIdFromCardLink();
    if (vid) {
      this.dataset.variantId = vid;
      const baseUrl =
        this.readCardProductUrl() ||
        this.dataset.url ||
        toRootPath(`/products/${this.itemHandle}`);
      this.dataset.url = appendVariantParam(baseUrl.split("?")[0], vid);
    }

    const image = this.readImageFromCardMedia();
    if (image) {
      this.dataset.image = image;
    }
  }

  applySwatchData(radio) {
    let { vid, vimg, url } = radio?.dataset || {};

    if (!vid) {
      vid = this.readVariantIdFromCardLink();
    }

    if (vid) {
      this.dataset.variantId = vid;
    }

    if (!vimg) {
      vimg = this.readImageFromCardMedia();
    }

    if (vimg) {
      this.dataset.image = vimg;
    }

    const baseUrl =
      url ||
      this.readCardProductUrl() ||
      this.dataset.url ||
      toRootPath(`/products/${this.itemHandle}`);
    this.dataset.url = vid ? appendVariantParam(baseUrl.split("?")[0], vid) : baseUrl;
  }

  readVariantIdFromCardLink() {
    const links = this.card?.querySelectorAll("[data-pr-url]");
    for (const link of links || []) {
      try {
        const variantId = new URL(link.href, window.location.origin).searchParams.get("variant");
        if (variantId) {
          return variantId;
        }
      } catch (error) {
        continue;
      }
    }

    return "";
  }

  readCardProductUrl() {
    return this.card?.querySelector("[data-pr-url]")?.href || "";
  }

  readImageFromCardMedia() {
    const media = this.card?.querySelector(".hdt-card-product__media--main");
    return media?.currentSrc || media?.getAttribute("src") || "";
  }

  async handleSwatchChange(event) {
    const radio = event.target;
    if (radio?.type !== "radio" || !radio.checked || !radio.closest("wrapp-hdt-swatches")) {
      return;
    }

    await this.applySwatchDataAsync(radio);

    if (snCompareStore.has(this.itemHandle)) {
      let item = this.buildItem();
      await enrichStoreItemVariant(item);
      item = this.buildItem();
      updateCompareStoreItem(this.itemHandle, item);
    }
  }

  async handleClick(event) {
    const selected = this.classList.contains("is-active");
    const disabled = this.classList.contains("is-disabled");
    if (disabled && !selected) {
      event.preventDefault();
      return;
    }

    await this.syncFromSelectedSwatchAsync();
    const wasTruncated = snCompareStore.getMode() === "truncated";
    const previousCount = snCompareStore.count();
    let item = this.buildItem();
    compareDebug("checkbox:toggle-before", item);
    const didToggle = snCompareStore.toggle(item);
    const nextCount = snCompareStore.count();

    if (
      didToggle &&
      wasTruncated &&
      previousCount === MAX_ITEMS - 1 &&
      nextCount === MAX_ITEMS
    ) {
      snCompareStore.setMode("expanded");
    }

    if (snCompareStore.has(this.itemHandle)) {
      item = this.buildItem();
      await enrichStoreItemVariant(item);
      item = this.buildItem();
      if (item.variantId) {
        updateCompareStoreItem(this.itemHandle, item);
      }
      compareDebug("checkbox:toggle-after", item);
    }
  }

  handleKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    this.click();
  }

  handleStoreChange() {
    this.syncFromStore();
  }

  syncFromStore() {
    const handle = this.itemHandle;
    if (!handle) {
      return;
    }

    const isSelected = snCompareStore.has(handle);
    const isExcludedPage = snCompareStore.isCompareDisabledCollectionPage(this.pageCollectionHandle);
    const isIncompatible =
      !isSelected && snCompareStore.count() > 0 && !snCompareStore.canAdd(this.itemCollections);
    const isLimitReached = !isSelected && snCompareStore.count() >= snCompareStore.MAX;
    const isDisabled = isExcludedPage || isIncompatible || isLimitReached;

    this.classList.toggle("is-active", isSelected);
    this.classList.toggle("is-disabled", isDisabled);
    this.setAttribute("aria-checked", isSelected ? "true" : "false");
    this.setAttribute("aria-disabled", isDisabled ? "true" : "false");
    this.tabIndex = isDisabled ? -1 : 0;

    if (isLimitReached && getCompareStrings().limitReached) {
      this.setAttribute("title", getCompareStrings().limitReached);
    } else {
      this.removeAttribute("title");
    }
  }
}

class SnCompareBar extends HTMLElement {
  constructor() {
    super();
    this.handleStoreChange = this.handleStoreChange.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleCompare = this.handleCompare.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.handleStackClick = this.handleStackClick.bind(this);
    this.handleStackKeydown = this.handleStackKeydown.bind(this);
    this.handleStackMouseEnter = this.handleStackMouseEnter.bind(this);
    this.handleStackMouseLeave = this.handleStackMouseLeave.bind(this);
    this.isStackHovered = false;
  }

  connectedCallback() {
    this.stack = this.querySelector("[data-compare-stack]");
    this.panelBg = this.querySelector("[data-compare-panel-bg]");
    this.header = this.querySelector("[data-compare-header]");
    this.footer = this.querySelector("[data-compare-footer]");
    this.toggleButton = this.querySelector("[data-compare-toggle]");
    this.toggleText = this.querySelector("[data-compare-toggle-text]");
    this.list = this.querySelector("[data-compare-list]");
    this.slots = Array.from(this.querySelectorAll("[data-compare-slot]"));
    this.clearButton = this.querySelector("[data-compare-clear]");
    this.compareButton = this.querySelector("[data-compare-cta]");

    if (!this.stack || !this.toggleButton || !this.list || !this.compareButton) {
      return;
    }

    this.toggleButton.addEventListener("click", this.handleToggle);
    this.clearButton?.addEventListener("click", this.handleClear);
    this.compareButton.addEventListener("click", this.handleCompare);
    this.list.addEventListener("click", this.handleRemove);
    this.stack.addEventListener("click", this.handleStackClick);
    this.stack.addEventListener("keydown", this.handleStackKeydown);
    this.stack.addEventListener("mouseenter", this.handleStackMouseEnter);
    this.stack.addEventListener("mouseleave", this.handleStackMouseLeave);
    document.addEventListener(CHANGE_EVENT, this.handleStoreChange);

    this.render({ animateModeChange: false, animatePresence: false });
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener("click", this.handleToggle);
    this.clearButton?.removeEventListener("click", this.handleClear);
    this.compareButton?.removeEventListener("click", this.handleCompare);
    this.list?.removeEventListener("click", this.handleRemove);
    this.stack?.removeEventListener("click", this.handleStackClick);
    this.stack?.removeEventListener("keydown", this.handleStackKeydown);
    this.stack?.removeEventListener("mouseenter", this.handleStackMouseEnter);
    this.stack?.removeEventListener("mouseleave", this.handleStackMouseLeave);
    document.removeEventListener(CHANGE_EVENT, this.handleStoreChange);
  }

  get compareBasePath() {
    return getCompareStrings().compareBase || "/pages/compare";
  }

  get removeLabelTemplate() {
    return getCompareStrings().removeLabel || "Remove [title] from comparison";
  }

  get hideLabel() {
    return getCompareStrings().hide || "Hide";
  }

  get showLabel() {
    return getCompareStrings().show || "Show";
  }

  get filledSlots() {
    return this.slots.filter((slot) => !slot.classList.contains("is-empty"));
  }

  get emptySlots() {
    return this.slots.filter((slot) => slot.classList.contains("is-empty"));
  }

  handleStoreChange(event) {
    const action = event?.detail?.action || "update";
    const animatePresence = ["add", "remove", "clear"].includes(action);
    const animateModeChange = action === "mode";

    this.render({ animatePresence, animateModeChange, action });
  }

  handleToggle(event) {
    event.stopPropagation();
    const currentMode = snCompareStore.getMode();
    const nextMode = currentMode === "expanded" ? "truncated" : "expanded";
    snCompareStore.setMode(nextMode);
  }

  handleStackClick(event) {
    if (snCompareStore.getMode() !== "truncated") {
      return;
    }

    if (event.target.closest("[data-compare-toggle], [data-compare-clear], [data-compare-cta], [data-compare-remove]")) {
      return;
    }

    event.preventDefault();
    snCompareStore.setMode("expanded");
  }

  handleStackMouseEnter() {
    if (
      snCompareStore.getMode() !== "truncated" ||
      this.filledSlots.length < 2 ||
      window.matchMedia("(hover: none)").matches ||
      this.isStackHovered
    ) {
      return;
    }

    this.stack.classList.add("is-hovered");
    this.animateStackHover(true);
  }

  handleStackMouseLeave(event) {
    if (!this.isStackHovered) {
      return;
    }

    const related = event.relatedTarget;
    if (related instanceof Node && this.stack.contains(related)) {
      return;
    }

    this.stack.classList.remove("is-hovered");
    this.animateStackHover(false);
  }

  handleStackKeydown(event) {
    if (snCompareStore.getMode() !== "truncated") {
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    snCompareStore.setMode("expanded");
  }

  handleClear(event) {
    event.stopPropagation();
    snCompareStore.clear();
  }

  async handleCompare(event) {
    event.preventDefault();

    const disabled = this.compareButton?.getAttribute("aria-disabled") === "true";
    if (disabled) {
      return;
    }

    const beforeItems = snCompareStore.getItems();
    compareDebug("compare:pre-enrich", beforeItems);

    await Promise.all(beforeItems.map((item) => enrichStoreItemVariant(item)));

    const compareUrl = snCompareStore.getCompareUrl(this.compareBasePath);
    const afterItems = snCompareStore.getItems();

    console.log("[sn-compare] compare:navigate", {
      compareUrl,
      items: afterItems.map((item) => ({
        handle: item.handle,
        variantId: item.variantId,
        image: item.image
      }))
    });

    window.location.assign(compareUrl);
  }

  handleRemove(event) {
    const button = event.target.closest("[data-compare-remove]");
    if (!button) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const handle = button.dataset.handle;
    if (handle) {
      snCompareStore.remove(handle);
    }
  }

  render(options = {}) {
    const { animatePresence = false, animateModeChange = false, action = "init" } = options;
    const items = snCompareStore.getItems();
    const mode = snCompareStore.getMode();
    const isEmpty = items.length === 0;
    const wasHidden = this.hidden;

    this.renderSlots(items);
    this.updateCompareButton(items);

    if (isEmpty) {
      if (!wasHidden && animatePresence && this.stack) {
        const exitAnimation = animate(this.stack, { opacity: [1, 0], y: [0, 24] }, { duration: 0.2 });
        exitAnimation.then(() => {
          this.hidden = true;
        });
      } else {
        this.hidden = true;
      }

      if (action !== "mode") {
        this.dataset.mode = "expanded";
        this.updateToggleButton("expanded");
      }
      return;
    }

    this.hidden = false;
    this.setMode(mode, animateModeChange);

    if (["add", "remove"].includes(action)) {
      if (mode === "truncated") {
        if (this.isStackHovered) {
          this.isStackHovered = false;
          this.stack?.classList.remove("is-hovered");
        }
        this.applyCollapsedStackLayout(false);
      } else {
        this.syncStackLayout(mode);
      }
    }

    if (wasHidden && animatePresence && action === "add") {
      this.animateIn();
    } else if (animatePresence && action === "remove" && items.length === 0) {
      this.animateOut();
    }
  }

  setMode(mode, shouldAnimate) {
    const nextMode = VALID_MODES.has(mode) ? mode : "expanded";

    if (nextMode !== "truncated") {
      this.isStackHovered = false;
      this.stack?.classList.remove("is-hovered");
    }

    this.dataset.mode = nextMode;
    this.updateToggleButton(nextMode);
    this.stack.tabIndex = nextMode === "truncated" ? 0 : -1;
    this.stack.setAttribute("role", nextMode === "truncated" ? "button" : "presentation");
    this.stack.setAttribute(
      "aria-label",
      nextMode === "truncated" ? this.showLabel : ""
    );

    this.animateMode(nextMode === "expanded" ? "open" : "closed", shouldAnimate);
  }

  syncStackLayout(mode = snCompareStore.getMode()) {
    const nextMode = VALID_MODES.has(mode) ? mode : "expanded";
    if (nextMode === "truncated") {
      this.applyCollapsedStackLayout(false);
      return;
    }
    this.animateMode("open", false);
  }

  applyCollapsedStackLayout(shouldAnimate = false, spring = STACK_SPRING, options = {}) {
    if (!this.list || snCompareStore.getMode() !== "truncated") {
      return;
    }

    const { isHoverTransition = false } = options;
    const metrics = this.getCollapsedStackMetrics(this.isStackHovered);
    const motionOptions = shouldAnimate ? spring : { duration: 0 };
    const collapseAnimations = [];

    if (!shouldAnimate && !isHoverTransition) {
      this.list.classList.remove("is-animating-collapse");
    }

    if (!isHoverTransition) {
      if (shouldAnimate) {
        this.list.classList.add("is-animating-collapse");
      } else {
        this.list.style.height = `${metrics.listHeight}px`;
        this.list.style.marginTop = "0px";
        this.list.style.paddingTop = `${metrics.listPaddingTop}px`;
      }

      if (shouldAnimate) {
        resetListMotionStyles(this.list);
        collapseAnimations.push(
          animate(
            this.list,
            {
              height: `${metrics.listHeight}px`,
              marginTop: 0,
              paddingTop: `${metrics.listPaddingTop}px`
            },
            motionOptions
          )
        );
      }
    }

    if (this.stack) {
      if (!isHoverTransition) {
        if (shouldAnimate) {
          animate(
            this.stack,
            {
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: 0,
              paddingRight: 0
            },
            motionOptions
          );
        } else {
          this.stack.style.paddingTop = "0px";
          this.stack.style.paddingBottom = "0px";
          this.stack.style.paddingLeft = "0px";
          this.stack.style.paddingRight = "0px";
        }
      }

      if (this.isStackHovered && metrics.stackHoverHitPad > 0) {
        this.stack.style.setProperty("--compare-stack-hover-hit-pad", `${metrics.stackHoverHitPad}px`);
      } else {
        this.stack.style.removeProperty("--compare-stack-hover-hit-pad");
      }
    }

    this.filledSlots.forEach((slot, index) => {
      const stackIndex = Number(slot.dataset.stackIndex) || index;
      const closedTransform = this.getSlotCollapsedTransform(stackIndex, metrics);

      if (!isHoverTransition) {
        resetSlotMotionStyles(slot);
      }

      const slotTarget = {
        ...closedTransform,
        marginTop: 0,
        marginBottom: 0
      };

      if (!isHoverTransition) {
        slotTarget.height = "auto";
      }

      if (shouldAnimate) {
        collapseAnimations.push(
          animate(
            slot,
            slotTarget,
            { ...motionOptions, delay: shouldAnimate && !isHoverTransition ? stackIndex * 0.04 : 0 }
          )
        );
      } else {
        slot.style.transform = `translateY(${slotTarget.y}px) scale(${slotTarget.scale})`;
        slot.style.opacity = `${slotTarget.opacity}`;
        slot.style.marginTop = "0px";
        slot.style.marginBottom = "0px";
        if (!isHoverTransition) {
          slot.style.height = "auto";
          slot.style.width = "100%";
        }
      }
      slot.style.zIndex = String(this.filledSlots.length - stackIndex);
      slot.style.pointerEvents = "none";
    });

    this.emptySlots.forEach((slot) => {
      resetSlotMotionStyles(slot);
      if (shouldAnimate) {
        collapseAnimations.push(
          animate(
            slot,
            {
              y: 0,
              scale: 1,
              opacity: 0,
              width: "100%",
              height: 0,
              marginTop: 0,
              marginBottom: 0
            },
            motionOptions
          )
        );
      } else {
        slot.style.transform = "translateY(0px) scale(1)";
        slot.style.opacity = "0";
        slot.style.width = "100%";
        slot.style.height = "0px";
        slot.style.marginTop = "0px";
        slot.style.marginBottom = "0px";
      }
      slot.style.pointerEvents = "none";
      slot.style.overflow = "hidden";
    });

    if (!isHoverTransition && shouldAnimate && collapseAnimations.length) {
      Promise.allSettled(collapseAnimations.map(toAnimationPromise)).finally(() => {
        this.list?.classList.remove("is-animating-collapse");
      });
    }
  }

  getCollapsedStackMetrics(isHovered = false) {
    const count = this.filledSlots.length;
    const maxPeek = STACK_PEEK + STACK_HOVER_PEEK;
    const peek = STACK_PEEK + (isHovered ? STACK_HOVER_PEEK : 0);
    const hoverLift = isHovered ? STACK_HOVER_LIFT : 0;
    const backIndex = Math.max(0, count - 1);
    const topReserve =
      backIndex > 0 ? backIndex * maxPeek + backIndex * STACK_HOVER_LIFT + STACK_TOP_CLEARANCE : 0;
    const listPaddingTop = topReserve;

    return {
      peek,
      hoverLift,
      listHeight: ITEM_HEIGHT + topReserve + STACK_BOTTOM_CLEARANCE,
      listPaddingTop,
      stackHoverHitPad: this.getStackHoverHitPad(count, { peek, hoverLift, listPaddingTop })
    };
  }

  /** Modest hit buffer above the list — covers fanned card tops without an oversized hover zone. */
  getStackHoverHitPad(count, metrics) {
    if (count < 2 || !metrics.hoverLift) {
      return 0;
    }

    return (
      (count - 1) * STACK_HOVER_PEEK + Math.max(0, count - 2) * Math.round(STACK_HOVER_LIFT / 2)
    );
  }

  getSlotCollapsedTransform(stackIndex, metrics) {
    const naturalTop = stackIndex * (ITEM_HEIGHT + ITEM_GAP);
    const targetTop =
      -stackIndex * metrics.peek - (stackIndex > 0 ? stackIndex * metrics.hoverLift : 0);

    return {
      y: targetTop - naturalTop,
      scale: 1 - stackIndex * (metrics.hoverLift ? 0.03 : 0.05),
      opacity: Math.max(1 - stackIndex * (metrics.hoverLift ? 0.15 : 0.3), metrics.hoverLift ? 0.72 : 0.5)
    };
  }

  animateStackHover(isHovered) {
    if (snCompareStore.getMode() !== "truncated") {
      return;
    }

    this.isStackHovered = isHovered;
    this.applyCollapsedStackLayout(true, HOVER_SPRING, { isHoverTransition: true });
  }

  animateMode(state, shouldAnimate) {
    const isOpen = state === "open";
    const motionOptions = shouldAnimate ? SPRING : { duration: 0 };
    const filledSlots = this.filledSlots;
    const emptySlots = this.emptySlots;

    if (this.panelBg) {
      animate(this.panelBg, { opacity: isOpen ? 1 : 0 }, motionOptions);
    }

    if (this.header) {
      animate(
        this.header,
        {
          y: isOpen ? 0 : 60,
          scale: isOpen ? 1 : 0.8,
          opacity: isOpen ? 1 : 0
        },
        {
          ...motionOptions,
          delay: isOpen && shouldAnimate ? 0.2 : 0
        }
      );
      this.header.style.pointerEvents = isOpen ? "auto" : "none";
    }

    if (this.footer) {
      animate(
        this.footer,
        {
          y: isOpen ? 0 : 16,
          opacity: isOpen ? 1 : 0
        },
        motionOptions
      );
      this.footer.style.pointerEvents = isOpen ? "auto" : "none";
    }

    if (this.stack) {
      animate(
        this.stack,
        {
          paddingTop: isOpen ? 16 : 0,
          paddingBottom: isOpen ? 16 : 0,
          paddingLeft: isOpen ? 16 : 0,
          paddingRight: isOpen ? 16 : 0
        },
        shouldAnimate ? STACK_SPRING : { duration: 0 }
      );
    }

    if (isOpen) {
      resetListMotionStyles(this.list);

      if (this.list) {
        animate(
          this.list,
          {
            height: "auto",
            marginTop: 40,
            paddingTop: 0
          },
          motionOptions
        );
      }

      filledSlots.forEach((slot) => {
        const stackIndex = Number(slot.dataset.stackIndex) || 0;
        const delay = shouldAnimate ? stackIndex * 0.04 : 0;

        resetSlotMotionStyles(slot);
        animate(
          slot,
          {
            y: 0,
            scale: 1,
            opacity: 1,
            width: "100%",
            height: "auto",
            marginTop: 0,
            marginBottom: 0
          },
          { ...motionOptions, delay }
        );

        slot.style.zIndex = String(filledSlots.length - stackIndex);
        slot.style.pointerEvents = "auto";
      });

      emptySlots.forEach((slot) => {
        resetSlotMotionStyles(slot);
        animate(
          slot,
          {
            y: 0,
            scale: 1,
            opacity: 1,
            width: "100%",
            height: "auto",
            marginTop: 0,
            marginBottom: 0
          },
          motionOptions
        );
        slot.style.pointerEvents = "auto";
        slot.style.overflow = "visible";
      });
      return;
    }

    this.applyCollapsedStackLayout(shouldAnimate);
  }

  updateToggleButton(mode) {
    const isExpanded = mode === "expanded";
    if (this.toggleText) {
      this.toggleText.textContent = isExpanded ? this.hideLabel : this.showLabel;
    }
    this.toggleButton?.setAttribute("aria-expanded", isExpanded ? "true" : "false");
  }

  updateCompareButton(items) {
    const canCompare = items.length >= 2 && items.length <= snCompareStore.MAX;
    const compareUrl = snCompareStore.getCompareUrl(this.compareBasePath);

    this.compareButton.setAttribute("href", compareUrl);
    this.compareButton.setAttribute("aria-disabled", canCompare ? "false" : "true");
    this.compareButton.classList.toggle("is-disabled", !canCompare);

    if (this.clearButton) {
      this.clearButton.disabled = items.length === 0;
    }
  }

  renderSlots(items) {
    let filledStackIndex = 0;

    this.slots.forEach((slot, index) => {
      const item = items[index];
      const link = slot.querySelector("[data-compare-link]");
      const image = slot.querySelector("[data-compare-image]");
      const title = slot.querySelector("[data-compare-title]");
      const removeButton = slot.querySelector("[data-compare-remove]");
      const empty = slot.querySelector("[data-compare-empty]");

      resetSlotMotionStyles(slot);

      if (!item) {
        slot.classList.add("is-empty");
        slot.removeAttribute("data-stack-index");
        link?.setAttribute("hidden", "");
        removeButton?.setAttribute("hidden", "");
        empty?.removeAttribute("hidden");
        if (removeButton) {
          removeButton.dataset.handle = "";
        }
        return;
      }

      slot.classList.remove("is-empty");
      slot.dataset.stackIndex = String(filledStackIndex);
      filledStackIndex += 1;
      empty?.setAttribute("hidden", "");
      link?.removeAttribute("hidden");
      removeButton?.removeAttribute("hidden");

      if (link) {
        link.setAttribute("href", item.url);
        link.setAttribute("tabindex", "0");
      }
      if (image) {
        image.src = item.image || "";
        image.alt = item.title;
      }
      if (title) {
        title.textContent = item.title;
      }
      if (removeButton) {
        removeButton.dataset.handle = item.handle;
        removeButton.setAttribute("aria-label", this.removeLabelTemplate.replace("[title]", item.title));
      }
    });
  }

  animateIn() {
    if (!this.stack) {
      return;
    }
    animate(
      this.stack,
      { opacity: [0, 1], y: [24, 0], scale: [0.96, 1] },
      { duration: 0.32, type: "spring", stiffness: 320, damping: 22 }
    );
  }

  animateOut() {
    if (!this.stack) {
      return;
    }
    const exitAnimation = animate(this.stack, { opacity: [1, 0], y: [0, 24] }, { duration: 0.2 });
    exitAnimation.then(() => {
      this.hidden = true;
    });
  }
}

if (!customElements.get("sn-compare-checkbox")) {
  customElements.define("sn-compare-checkbox", SnCompareCheckbox);
}

if (!customElements.get("sn-compare-bar")) {
  customElements.define("sn-compare-bar", SnCompareBar);
}

export {
  snCompareStore,
  CHANGE_EVENT,
  MAX_ITEMS,
  toRootPath,
  sanitizeHandle,
  parseHandlesFromUrl,
  syncUrlFromStore,
  parseCollectionHandles
} from "./sn-compare-store.js";
